import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@/shared.module';
import type { NnnRegistrationInstrument } from '@/api';
import type { EditNnnInstrumentsDialogData, NnnInstrumentOptionGroup, SaveState } from '../../types/careplan-nnn.types';
import { reuseOrCreateByRef } from '../../utils/reuse-or-create-by-ref';
import { EditNnnInstrumentsDialogComponent } from '../edit-nnn-instruments-dialog/edit-nnn-instruments-dialog.component';

/**
 * Instruments block of a careplan-NNN row: the app functions picked as
 * measurement instruments, with a button opening a category-grouped
 * multi-select dialog. Selected functions no longer offered by the
 * institution stay listed (with a fallback label) so they survive an edit
 * round-trip. Emits the updated `instruments` array; the parent owns the row
 * and persists it.
 */
@Component({
  selector: 'app-nnn-instruments-section',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './nnn-instruments-section.component.html',
  styleUrl: './nnn-instruments-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NnnInstrumentsSectionComponent {
  private readonly dialog = inject(MatDialog);

  readonly instruments = input.required<NnnRegistrationInstrument[]>();
  readonly appFunctionGroups = input<NnnInstrumentOptionGroup[]>([]);
  readonly editable = input(false);
  readonly saveState = input<SaveState>('');

  readonly instrumentsChange = output<NnnRegistrationInstrument[]>();

  private readonly titleById = computed(
    () =>
      new Map(
        this.appFunctionGroups()
          .flatMap(group => group.functions)
          .map(appFunction => [appFunction.id, appFunction.title])
      )
  );

  protected readonly instrumentViews = computed(() =>
    this.instruments().map(instrument => ({
      instrument,
      title: this.titleById().get(instrument.appFunctionId) ?? null,
    }))
  );

  protected async openEdit(): Promise<void> {
    const data: EditNnnInstrumentsDialogData = {
      groups: this.appFunctionGroups(),
      selectedIds: this.instruments().map(instrument => instrument.appFunctionId),
    };
    const dialogRef = this.dialog.open(EditNnnInstrumentsDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      panelClass: ['card-primary', 'nnn-edit-dialog-panel'],
      data,
    });
    const chosenIds: string[] | undefined = await firstValueFrom(dialogRef.afterClosed());
    if (!chosenIds) {
      return;
    }
    this.instrumentsChange.emit(
      reuseOrCreateByRef(
        chosenIds,
        this.instruments(),
        instrument => instrument.appFunctionId,
        ref => ({ id: null, appFunctionId: ref })
      )
    );
  }
}
